/* eslint-disable @typescript-eslint/no-explicit-any */
import { Logger } from '@hashnode/file-parsing'
import fs from 'fs'
import path from 'path'
import stripAnsi from 'strip-ansi'

export class FileLogger implements Logger {
	private logFile: string
	private buffer: string[] = []
	private baseDir: string

	static getFilePath(cwd: string): string {
		return path.resolve(`${cwd}/hashnode-check-result.log`)
	}

	constructor(cwd: string) {
		this.logFile = FileLogger.getFilePath(cwd)
		this.baseDir = path.resolve(cwd)
	}

	private addToBuffer(level: string, message: string, rest?: any): void {
		const timestamp = new Date().toISOString()
		let logEntry = `[${timestamp}] [${level}] ${stripAnsi(message)}`

		if (rest && typeof rest === 'object' && 'file' in rest) {
			const { file, line, column } = rest
			const formattedPath = this.formatFilePath(file, line, column)
			logEntry += ` ${formattedPath}`
		} else if (rest) {
			logEntry += ' ' + JSON.stringify(rest)
		}

		this.buffer.push(logEntry)
	}

	private formatFilePath(filePath: string, line?: number, column?: number): string {
		const relativePath = path.relative(this.baseDir, filePath)
		let formattedPath = `${relativePath}`
		if (line !== undefined) {
			formattedPath += `:${line}`
			if (column !== undefined) {
				formattedPath += `:${column}`
			}
		}
		return formattedPath
	}

	writeBufferToFile(): void {
		const content = this.buffer.join('\n') + '\n'
		fs.writeFileSync(this.logFile, '')
		fs.appendFileSync(this.logFile, content)

		this.buffer = [] // Clear the buffer after writing
	}

	log(message: string, rest?: any): void {
		this.addToBuffer('LOG', message, rest)
	}

	info(message: string, rest?: any): void {
		this.addToBuffer('INFO', message, rest)
	}

	success(message: string, rest?: any): void {
		this.addToBuffer('SUCCESS', message, rest)
	}

	warn(message: string, rest?: any): void {
		this.addToBuffer('WARN', message, rest)
	}

	error(message: string, rest?: any): void {
		this.addToBuffer('ERROR', message, rest)
	}

	debug(message: string, rest?: any): void {
		this.addToBuffer('DEBUG', message, rest)
	}

	time(label?: string): void {
		this.addToBuffer('TIME', `Started: ${label || 'default'}`)
	}

	timeEnd(label?: string): void {
		this.addToBuffer('TIME', `Ended: ${label || 'default'}`)
	}

	clear(): void {
		this.buffer = []
		if (fs.existsSync(this.logFile)) {
			fs.unlinkSync(this.logFile)
		}
	}

	exit(message: string | Error, code?: number): never {
		const errorMessage = message instanceof Error ? message.stack : message
		this.error('EXIT', stripAnsi(errorMessage ?? ''))
		this.writeBufferToFile() // Write buffer to file before exiting
		process.exit(code || 1)
	}
}
